import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Landing from './pages/Landing';
import ProfileSetup from './pages/ProfileSetup';
import Discover from './pages/Discover';
import Matches from './pages/Matches';
import Chat from './pages/Chat';
import Settings from './pages/Settings';
import UserProfile from './pages/UserProfile';
import Leaderboard from './pages/Leaderboard';
import CallScreen from './pages/CallScreen';
import SearchPage from './pages/SearchPage';
import FeedPage from './pages/FeedPage';
import Friends from './pages/Friends';
import AdminDashboard from './pages/AdminDashboard';
import GamesPage from './pages/GamesPage';
import GameInvitePage from './pages/GameInvitePage';
import GameSession from './pages/GameSession';
import UsAgainGame from './pages/UsAgainGame';
import BuyVotesPage from './pages/BuyVotesPage';
import IncomingCallHandler from './components/IncomingCallHandler';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to include credentials with all requests
axios.defaults.withCredentials = true;

const AuthGuard = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    
    const checkAuth = async () => {
      // Set a max timeout of 10 seconds
      timeoutId = setTimeout(() => {
        if (isMounted && loading) {
          console.log('Auth check timeout - redirecting to login');
          setAuthenticated(false);
          setLoading(false);
        }
      }, 10000);
      
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        // Get referral code from localStorage (set by Landing page)
        const storedReferralCode = localStorage.getItem('referral_code');
        try {
          await axios.post(`${API}/auth/session`, { 
            session_id: sessionId,
            referral_code: storedReferralCode || null
          }, {
            withCredentials: true
          });
          // Clear referral code from localStorage after successful signup
          localStorage.removeItem('referral_code');
          window.location.hash = '';
          clearTimeout(timeoutId);
          if (isMounted) {
            setAuthenticated(true);
            setLoading(false);
          }
          return;
        } catch (e) {
          console.error('Session processing failed:', e);
        }
      }

      // Check existing session
      try {
        const response = await axios.get(`${API}/auth/me`, { 
          withCredentials: true,
          timeout: 3000
        });
        clearTimeout(timeoutId);
        if (isMounted) {
          setAuthenticated(true);
          setLoading(false);
        }
      } catch (e) {
        console.log('Not authenticated:', e.message);
        clearTimeout(timeoutId);
        if (isMounted) {
          setAuthenticated(false);
          setLoading(false);
        }
      }
    };

    checkAuth();
    
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navigate, location.pathname]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <IncomingCallHandler />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/setup" element={<AuthGuard><ProfileSetup /></AuthGuard>} />
          <Route path="/discover" element={<AuthGuard><Discover /></AuthGuard>} />
          <Route path="/search" element={<AuthGuard><SearchPage /></AuthGuard>} />
          <Route path="/feed" element={<AuthGuard><FeedPage /></AuthGuard>} />
          <Route path="/friends" element={<AuthGuard><Friends /></AuthGuard>} />
          <Route path="/profile/:userId" element={<AuthGuard><UserProfile /></AuthGuard>} />
          <Route path="/matches" element={<AuthGuard><Matches /></AuthGuard>} />
          <Route path="/chat/:matchId" element={<AuthGuard><Chat /></AuthGuard>} />
          <Route path="/call/:matchId/:callType" element={<AuthGuard><CallScreen /></AuthGuard>} />
          <Route path="/settings" element={<AuthGuard><Settings /></AuthGuard>} />
          <Route path="/leaderboard" element={<AuthGuard><Leaderboard /></AuthGuard>} />
          <Route path="/buy-votes" element={<AuthGuard><BuyVotesPage /></AuthGuard>} />
          <Route path="/games" element={<AuthGuard><GamesPage /></AuthGuard>} />
          <Route path="/us-again" element={<AuthGuard><UsAgainGame /></AuthGuard>} />
          <Route path="/games/invite/:gameId" element={<AuthGuard><GameInvitePage /></AuthGuard>} />
          <Route path="/games/session/:sessionId" element={<AuthGuard><GameSession /></AuthGuard>} />
          <Route path="/admin" element={<AuthGuard><AdminDashboard /></AuthGuard>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
